import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, AlertTriangle, X, Check, Clock } from "lucide-react";
import MoneyTabs from "@/components/coach/MoneyTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UnsettledIous — the coach-facing IOU reconciliation page.
 *
 * Sachin 2026-02-28: IOUs are the safety net for the rare case where an
 * athlete gets checked in but their parent has no active credit source.
 * The atomic-check-in guarantee writes an IOU row so nothing falls
 * through the cracks. Owner can (a) leave them alone — the next pack
 * purchase auto-settles the oldest ones, or (b) manually waive if the
 * IOU should never be charged.
 */
export default function UnsettledIous() {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        try {
            const r = await axios.get(`${API}/coach/unsettled-ious`, { withCredentials: true });
            setRows(r.data.ious || []);
        } catch (e) {
            toast.error("Couldn't load IOUs", {
                description: e?.response?.data?.detail || "Try again in a moment.",
            });
            setRows([]);
        }
    };
    useEffect(() => { load(); }, []);

    const waive = async (debit_id) => {
        if (!window.confirm("Waive this IOU? It becomes a comp — no charge, no future auto-settle.")) return;
        setBusy(debit_id);
        try {
            await axios.post(`${API}/coach/unsettled-ious/resolve`,
                { debit_id, action: "waive" },
                { withCredentials: true });
            toast.success("IOU waived");
            setRows((r) => r.filter((x) => x.debit_id !== debit_id));
        } catch (e) {
            toast.error("Couldn't waive", { description: e?.response?.data?.detail });
        } finally {
            setBusy(null);
        }
    };

    if (rows === null) {
        return (
            <div className="flex items-center justify-center p-8 text-zinc-400">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading IOUs…
            </div>
        );
    }

    return (
        <div data-testid="unsettled-ious-page" className="max-w-5xl mx-auto p-6">
            <MoneyTabs />
            <div className="mb-6">
                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-1">Reconciliation</div>
                <h1 className="font-display text-2xl tracking-wider">Unsettled IOUs</h1>
                <p className="text-xs text-zinc-400 mt-1 max-w-xl">
                    Sessions checked in when the parent had no active credit source.
                    The next pack purchase auto-settles the oldest IOUs first. Waive
                    if the IOU should never be charged.
                </p>
            </div>

            {rows.length === 0 ? (
                <div
                    data-testid="unsettled-ious-empty"
                    className="border border-emerald-800/50 bg-emerald-950/20 rounded-xl p-6 text-emerald-300 text-sm flex items-center gap-2"
                >
                    <Check className="w-4 h-4" /> No unsettled IOUs. The check-in ledger is clean.
                </div>
            ) : (
                <div className="border border-zinc-800 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-zinc-950 text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                            <tr>
                                <th className="text-left py-3 px-4">Athlete</th>
                                <th className="text-left py-3 px-4">Parent</th>
                                <th className="text-left py-3 px-4">Occurred</th>
                                <th className="text-right py-3 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr
                                    key={r.debit_id}
                                    data-testid={`iou-row-${r.debit_id}`}
                                    className="border-t border-zinc-900"
                                >
                                    <td className="py-3 px-4 text-zinc-200">
                                        {r.athlete_name || <span className="text-zinc-500">—</span>}
                                    </td>
                                    <td className="py-3 px-4">
                                        <div className="text-zinc-200">{r.parent_name || r.parent_email || "—"}</div>
                                        {r.parent_email && (
                                            <div className="text-[11px] text-zinc-500">{r.parent_email}</div>
                                        )}
                                    </td>
                                    <td className="py-3 px-4">
                                        <div className="flex items-center gap-2 text-zinc-300">
                                            <Clock className="w-3 h-3 text-zinc-500" />
                                            {r.days_ago === 0 ? "Today" :
                                                r.days_ago === 1 ? "1 day ago" :
                                                    `${r.days_ago}d ago`}
                                        </div>
                                        {r.days_ago > 30 && (
                                            <div className="text-[11px] text-amber-400 mt-0.5 flex items-center gap-1">
                                                <AlertTriangle className="w-3 h-3" /> Aging
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        <button
                                            data-testid={`iou-waive-${r.debit_id}`}
                                            onClick={() => waive(r.debit_id)}
                                            disabled={busy === r.debit_id}
                                            className="text-[10px] tracking-[0.2em] uppercase border border-zinc-800 text-zinc-300 hover:border-brand-red hover:text-white px-3 py-1.5 disabled:opacity-50 transition-colors"
                                        >
                                            {busy === r.debit_id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <X className="w-3 h-3 inline mr-1" />}
                                            Waive
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
